import {
  BorrowingPowerPageError,
  BorrowingPowerPageResult,
  BorrowingPowerPageResultTypenames,
  BorrowingPowerPageSuccess,
  BorrowingPowerProductType,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useBorrowingPowerPage from 'features/advance/hooks/useBorrowingPowerPage';
import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedOrCoreError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

export interface ExtendedBorrowingPowerPageSuccess extends BorrowingPowerPageSuccess {
  borrowingPowerPageTitle: string;
}

const isExtendedBorrowingPowerPageSuccess = (
  page: BorrowingPowerPageResult | undefined,
): page is ExtendedBorrowingPowerPageSuccess => {
  return (
    page?.__typename ===
      BorrowingPowerPageResultTypenames.BorrowingPowerPageSuccess &&
    'borrowingPowerPageTitle' in page
  );
};

const isBorrowingPowerPageError = (
  page: BorrowingPowerPageResult | undefined,
): page is BorrowingPowerPageError => {
  return (
    page?.__typename ===
    BorrowingPowerPageResultTypenames.BorrowingPowerPageError
  );
};

const IncreaseYourLineOfCreditLimits = () => {
  const router = useRouter();
  const { borrowingPowerPage, error, fetching, refetch } =
    useBorrowingPowerPage(BorrowingPowerProductType.Vloc);
  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();
  const formattedError = useFormattedCombinedOrCoreError(error);
  const { bottom } = useSafeAreaInsets();

  const hasBorrowingPowerError = isBorrowingPowerPageError(borrowingPowerPage);

  const isBorrowingPowerSuccess =
    borrowingPowerPage?.__typename ===
    BorrowingPowerPageResultTypenames.BorrowingPowerPageSuccess;

  const analytics = parseOptionalGQLAnalyticsEvent(
    isBorrowingPowerSuccess ? borrowingPowerPage.displayAnalytics : undefined,
  );
  useViewDidFocusLogScreenAnalytics(analytics);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {isExtendedBorrowingPowerPageSuccess(borrowingPowerPage) ? (
        <View style={styles.parentContainer}>
          <ScrollView
            style={styles.parentContainer}
            contentContainerStyle={styles.scrollContainer}
            showsVerticalScrollIndicator={false}
          >
            <TitleLgText style={styles.titleText}>
              {borrowingPowerPage.borrowingPowerPageTitle}
            </TitleLgText>

            <BodyBaseText style={styles.subTitleText}>
              {borrowingPowerPage.subTitle}
            </BodyBaseText>

            {borrowingPowerPage.content.length > 0 ? (
              <View>
                {borrowingPowerPage.content.map((content) => (
                  <View style={styles.contentList} key={content.id}>
                    {content.title ? (
                      <BodyBaseBoldText>{content.title}</BodyBaseBoldText>
                    ) : null}
                    {content.subTitle ? (
                      <BodyBaseText style={styles.contentListSubtitle}>
                        {content.subTitle}
                      </BodyBaseText>
                    ) : null}
                    {content.contentList &&
                    content.contentList.content?.length > 0 ? (
                      <ContentList
                        contentList={content.contentList.content.map((item) => (
                          <BodyBaseText numberOfLines={0} key={item}>
                            {item}
                          </BodyBaseText>
                        ))}
                      />
                    ) : null}
                  </View>
                ))}
              </View>
            ) : null}
            {borrowingPowerPage.disclaimer ? (
              <BodyXSText style={styles.disclaimerText}>
                {borrowingPowerPage.disclaimer}
              </BodyXSText>
            ) : null}
          </ScrollView>
        </View>
      ) : null}

      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          formattedError || hasBorrowingPowerError ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={
                hasBorrowingPowerError
                  ? borrowingPowerPage.title
                  : (formattedError?.title ?? defaultTitle)
              }
              messageBody={
                hasBorrowingPowerError
                  ? borrowingPowerPage.message
                  : (formattedError?.message ?? defaultMessage)
              }
              primaryButton={{
                title: retryButtonTitle,
                onPress: () => refetch({ requestPolicy: 'network-only' }),
              }}
              secondaryButton={{
                title: cancelButtonTitle,
                onPress: () => {
                  if (router.canGoBack()) {
                    router.back();
                  }
                },
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentList: {
    paddingTop: theme.spacing.m,
  },
  contentListSubtitle: {
    paddingBottom: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  disclaimerText: {
    alignSelf: 'stretch',
    color: theme.color.text.secondary,
    paddingTop: theme.spacing.m,
  },
  parentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    padding: theme.spacing.m,
  },
  subTitleText: { alignSelf: 'stretch' },
  titleText: { alignSelf: 'stretch', paddingBottom: theme.spacing.m },
});

export default IncreaseYourLineOfCreditLimits;
